import React from 'react';
import { Link } from 'react-router-dom';
import FooterOne from '../common/footer/FooterOne';
import HeaderOne from '../common/header/HeaderOne';
import SEO from '../common/SEO';
import BcrumbBannerOne from '../elements/breadcrumb/BcrumbBannerOne';
import ColorSwitcher from '../elements/switcher/ColorSwitcher';
import SectionTitle from '../elements/section-title/SectionTitle';
import { FaShieldAlt, FaCode, FaServer, FaFileAlt, FaTools, FaLock, FaClipboardCheck } from 'react-icons/fa';

const servicesData = [
    {
        id: 1,
        icon: <FaShieldAlt />,
        title: "Web Application Penetration Testing",
        description: "End-to-end security assessment of web applications including authentication flows, session management, input validation, access control, and business logic testing."
    },
    {
        id: 2,
        icon: <FaLock />,
        title: "API Security Assessment",
        description: "Comprehensive testing of REST and GraphQL APIs for authorization bypass, injection attacks, rate limiting, and data exposure vulnerabilities."
    },
    {
        id: 3,
        icon: <FaCode />,
        title: "Source Code Review (SAST)",
        description: "Manual and static analysis of application source code to identify security weaknesses at the code level before deployment."
    },
    {
        id: 4,
        icon: <FaServer />,
        title: "Configuration & Infrastructure Audit",
        description: "Review of server configurations, database security, CORS policies, SSL/TLS implementation, and environment hardening."
    },
    {
        id: 5,
        icon: <FaFileAlt />,
        title: "Compliance-Ready VAPT Reports",
        description: "Detailed assessment reports with CVSS scoring, OWASP mapping, remediation guidance, and executive summaries — suitable for audit submissions and regulatory compliance."
    },
    {
        id: 6,
        icon: <FaTools />,
        title: "Remediation Support & Re-validation",
        description: "We work with your development team to fix identified issues and perform re-testing to verify that all remediations are effective."
    }
];

const methodologyData = [
    {
        phase: "Phase 1",
        title: "Reconnaissance & Scanning",
        description: "Application mapping, technology fingerprinting, endpoint enumeration, and automated vulnerability scanning."
    },
    {
        phase: "Phase 2",
        title: "Manual Penetration Testing",
        description: "In-depth manual testing of authentication, authorization, business logic, file uploads, payment flows, and API endpoints."
    },
    {
        phase: "Phase 3",
        title: "Remediation & Re-validation",
        description: "Collaboration with your dev team to fix issues, followed by complete re-testing to confirm all vulnerabilities are resolved."
    },
    {
        phase: "Phase 4",
        title: "Reporting & Sign-off",
        description: "Comprehensive report with findings, CVSS scores, OWASP mapping, and remediation verification log."
    }
];

const techData = [
    { category: "Backend", items: "Laravel (PHP), Node.js, Django, Spring Boot, .NET" },
    { category: "Frontend", items: "React, Next.js, Angular, Vue.js" },
    { category: "Mobile", items: "Android (Kotlin/Java), iOS (Swift), Flutter, React Native" },
    { category: "Database", items: "MySQL, PostgreSQL, MongoDB" },
    { category: "Cloud", items: "AWS, Azure, Google Cloud" },
    { category: "APIs", items: "REST, GraphQL, WebSocket" }
];

const industriesData = [
    "E-Commerce & Retail",
    "Healthcare & MedTech",
    "Financial Services & FinTech",
    "Education & EdTech",
    "SaaS Platforms",
    "Real Estate & PropTech"
];


const CybersecurityVapt = () => {

    return (
        <>
            <SEO title="Cybersecurity & VAPT Services" />
            <ColorSwitcher />
            <main className="main-wrapper">
                <HeaderOne />
                <BcrumbBannerOne
                    title="Application Security You Can Trust"
                    paragraph="We help businesses identify and fix security vulnerabilities before attackers do. Our VAPT services cover web applications, REST APIs, and mobile backends — aligned with global security standards."
                    styleClass=""
                    mainThumb="/images/banner/cyber-security.png"
                />

                {/* Section 1 - What We Offer */}
                <div className="section section-padding">
                    <div className="container">
                        <SectionTitle
                            subtitle="What We Offer"
                            title="Vulnerability Assessment & Penetration Testing (VAPT)"
                            description="Our security team performs thorough assessment of your application's security posture using a combination of automated scanning and manual penetration testing techniques."
                            textAlignment="heading-left mb--40"
                            textColor=""
                        />
                        <div className="row">
                            <div className="col-lg-12">
                                <div className="about-standards">
                                    <h5 style={{ marginBottom: '15px', color: 'var(--color-primary)' }}>We test against globally recognized standards:</h5>
                                    <div className="row">
                                        <div className="col-md-6">
                                            <ul className="list-unstyled" style={{ lineHeight: '2.2' }}>
                                                <li><FaClipboardCheck style={{ color: 'var(--color-primary)', marginRight: '10px' }} /> OWASP Top 10 (2021 Edition)</li>
                                                <li><FaClipboardCheck style={{ color: 'var(--color-primary)', marginRight: '10px' }} /> OWASP ASVS v4.0.3</li>
                                            </ul>
                                        </div>
                                        <div className="col-md-6">
                                            <ul className="list-unstyled" style={{ lineHeight: '2.2' }}>
                                                <li><FaClipboardCheck style={{ color: 'var(--color-primary)', marginRight: '10px' }} /> NIST SP 800-53 Rev 5</li>
                                                <li><FaClipboardCheck style={{ color: 'var(--color-primary)', marginRight: '10px' }} /> SANS Top 25 Most Dangerous Software Weaknesses</li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Section 2 - Our Security Services */}
                <div className="section section-padding bg-color-light">
                    <div className="container">
                        <SectionTitle
                            subtitle="Security Services"
                            title="Our Security Services"
                            description=""
                            textAlignment="heading-left mb--40"
                            textColor=""
                        />
                        <div className="row">
                            {servicesData.map((data) => (
                                <div className="col-lg-4 col-md-6" key={data.id}>
                                    <div className="services-grid service-style-2" style={{ marginBottom: '30px' }}>
                                        <div className="thumbnail">
                                            <span className="icon" style={{ fontSize: '36px', color: 'var(--color-primary)', display: 'block', marginBottom: '20px' }}>
                                                {data.icon}
                                            </span>
                                        </div>
                                        <div className="content">
                                            <h5 className="title">{data.title}</h5>
                                            <p>{data.description}</p>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Section 3 - Our Methodology */}
                <div className="section section-padding">
                    <div className="container">
                        <SectionTitle 
                            subtitle="Our Approach"
                            title="Our Testing Methodology"
                            description=""
                            textAlignment="heading-left mb--40"
                            textColor=""
                        />
                        <div className="row">
                            {methodologyData.map((data, index) => (
                                <div className="col-lg-6 col-md-6" key={index}>
                                    <div style={{
                                        marginBottom: '30px',
                                        padding: '30px',
                                        background: '#f8f8f8',
                                        borderRadius: '12px',
                                        borderTop: '4px solid var(--color-primary)',
                                        position: 'relative'
                                    }}>
                                        <span style={{
                                            display: 'inline-block',
                                            width: '45px',
                                            height: '45px',
                                            lineHeight: '45px',
                                            textAlign: 'center',
                                            borderRadius: '50%',
                                            background: 'var(--color-primary)',
                                            color: '#fff',
                                            fontSize: '18px',
                                            fontWeight: '700',
                                            marginBottom: '15px'
                                        }}>{index + 1}</span>
                                        <h5 style={{color: '#292930', marginBottom: '10px', fontSize: '18px', fontWeight: '600'}}>{data.phase} — {data.title}</h5>
                                        <p style={{color: '#525260', marginBottom: '0', fontSize: '15px', lineHeight: '1.7'}}>{data.description}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Section 4 - Technologies We Secure */}
                <div className="section section-padding bg-color-dark">
                    <div className="container">
                        <SectionTitle
                            subtitle="Technology Coverage"
                            title="Technologies We Secure"
                            description="We have experience securing applications built with a wide range of modern technologies."
                            textAlignment="heading-left heading-light-left mb--40"
                            textColor=""
                        />
                        <div className="row">
                            {techData.map((tech, index) => (
                                <div className="col-lg-4 col-md-6" key={index}>
                                    <div style={{
                                        padding: '25px',
                                        marginBottom: '20px',
                                        background: 'rgba(255,255,255,0.05)',
                                        borderRadius: '12px',
                                        border: '1px solid rgba(255,255,255,0.1)'
                                    }}>
                                        <h6 style={{ color: 'var(--color-primary)', marginBottom: '8px', fontSize: '14px', textTransform: 'uppercase', letterSpacing: '1px' }}>{tech.category}</h6>
                                        <p style={{ color: '#ccc', marginBottom: '0', fontSize: '15px' }}>{tech.items}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                    <ul className="list-unstyled shape-group-10">
                        <li className="shape shape-1"><img src={process.env.PUBLIC_URL + "/images/others/circle-1.png"} alt="Circle" /></li>
                        <li className="shape shape-2"><img src={process.env.PUBLIC_URL + "/images/others/line-3.png"} alt="Circle" /></li>
                        <li className="shape shape-3"><img src={process.env.PUBLIC_URL + "/images/others/bubble-5.png"} alt="Circle" /></li>
                    </ul>
                </div>

                {/* Section 5 - Industries We Serve */}
                <div className="section section-padding">
                    <div className="container">
                        <SectionTitle
                            subtitle="Industries"
                            title="Industries We Serve"
                            description=""
                            textAlignment="heading-left mb--40"
                            textColor=""
                        />
                        <div className="row">
                            {industriesData.map((industry, index) => (
                                <div className="col-lg-4 col-md-6" key={index}>
                                    <div style={{
                                        padding: '20px 25px',
                                        marginBottom: '20px',
                                        background: '#f8f8f8',
                                        borderRadius: '10px',
                                        borderLeft: '4px solid var(--color-primary)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '12px'
                                    }}>
                                        <FaShieldAlt style={{ color: 'var(--color-primary)', fontSize: '18px', flexShrink: 0 }} />
                                        <span style={{ fontSize: '16px', fontWeight: '500' }}>{industry}</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* CTA Section */}
                <div className="section call-to-action-area">
                    <div className="container">
                        <div className="call-to-action">
                            <div className="section-heading heading-light">
                                <span className="subtitle">READY TO SECURE YOUR APPLICATION?</span>
                                <h2 className="title">Get in touch for a free initial consultation</h2>
                                <p style={{ color: 'rgba(255,255,255,0.8)', maxWidth: '600px', margin: '0 auto 30px' }}>
                                    We'll assess your needs and provide a tailored VAPT proposal.
                                </p>
                                <Link to={process.env.PUBLIC_URL + "/contact"} className="axil-btn btn-large btn-fill-white">
                                    Request a Security Assessment
                                </Link>
                            </div>
                        </div>
                    </div>
                    <ul className="list-unstyled shape-group-9">
                        <li className="shape shape-1"><img src={process.env.PUBLIC_URL + "/images/others/bubble-12.png"} alt="Comments" /></li>
                        <li className="shape shape-2"><img src={process.env.PUBLIC_URL + "/images/others/bubble-16.png"} alt="Comments" /></li>
                        <li className="shape shape-3"><img src={process.env.PUBLIC_URL + "/images/others/bubble-13.png"} alt="Comments" /></li>
                        <li className="shape shape-4"><img src={process.env.PUBLIC_URL + "/images/others/bubble-14.png"} alt="Comments" /></li>
                        <li className="shape shape-5"><img src={process.env.PUBLIC_URL + "/images/others/bubble-16.png"} alt="Comments" /></li>
                        <li className="shape shape-6"><img src={process.env.PUBLIC_URL + "/images/others/bubble-15.png"} alt="Comments" /></li>
                        <li className="shape shape-7"><img src={process.env.PUBLIC_URL + "/images/others/bubble-16.png"} alt="Comments" /></li>
                    </ul>
                </div>

                <FooterOne parentClass="" />
            </main>
        </>
    )
}

export default CybersecurityVapt;
